import type { components } from '../../api/modelos'

export type LineaPedido = components['schemas']['LineaPedido']
export type LineaHistorial = components['schemas']['LineaHistorial']
export type Historial = components['schemas']['Historial']

/**
 * La salvaguarda del diseno: el precio se toma del catalogo de Odoo, y el
 * catalogo esta desfasado. Un BONO 4 figura a 32 EUR y lo que el cliente pago
 * la ultima vez fueron 31,82 de base (35 con IVA). Aqui se emparejan las lineas
 * del pedido que se va a publicar con las del ultimo pedido de ese cliente,
 * para que la diferencia se vea **antes** de publicar.
 *
 * Regla que gobierna el modulo: **no se adivina**. Si una linea no encuentra su
 * pareja, se queda sin comparar y se dice; inventar una equivalencia haria
 * pasar por "igual" un precio que nadie ha comprobado.
 */

export interface ComparacionLinea {
  nombre: string
  cantidad: number
  /** El que se publicara: el del catalogo, o el que haya escrito el administrador. */
  precio: number
  /** Lo que se cobro la ultima vez. `null` cuando no hay con que comparar. */
  precioAnterior: number | null
  difiere: boolean
  subtotal: number
}

/** Medio centimo: por debajo de eso es ruido de coma flotante, no un cambio. */
const TOLERANCIA = 0.005

function sinTildes(valor: string): string {
  return valor.normalize('NFD').replace(/\p{Diacritic}/gu, '')
}

/**
 * Las piezas comparables de un nombre de producto, con el mismo criterio que
 * `_tokens` del backend: sin tildes, en mayusculas, solo alfanumerico y el
 * punto decimal -que en `18.9L` **es** parte del nombre-, y la `L` suelta
 * pegada al numero anterior.
 */
function piezas(nombre: string): string[] {
  const texto = sinTildes(nombre).toUpperCase().replace(/,/g, '.')
  const salida: string[] = []
  for (const trozo of texto.split(/\s+/)) {
    const limpio = trozo.replace(/[^A-Z0-9.]/g, '').replace(/^\.+|\.+$/g, '')
    if (limpio === '') continue
    const ultimo = salida[salida.length - 1]
    if (limpio === 'L' && ultimo !== undefined && /^\d/.test(ultimo)) {
      salida[salida.length - 1] = `${ultimo}L`
    } else {
      salida.push(limpio)
    }
  }
  return salida
}

function clave(nombre: string): string {
  return [...new Set(piezas(nombre))].sort().join(' ')
}

/**
 * La linea del pedido anterior que corresponde a esta. Dos pasadas, en orden:
 *
 * 1. **Por `product_id`.** Es lo unico que no miente: `sale.order.line.name` es
 *    la descripcion del pedido y lleva referencias, notas y saltos de linea.
 * 2. **Por nombre exacto**, comparando conjuntos de palabras. Hace falta porque
 *    el flujo actual crea algunos extras sin `product_id`.
 *
 * No hay tercera pasada por inclusion: `{ENVASE, MANANTIAL, 18.9L}` esta contenido
 * en `{DEVOLUCION, ENVASE, MANANTIAL, 18.9L}`, asi que emparejar por inclusion
 * pondria el precio de la devolucion en el envase.
 */
function emparejar(
  linea: LineaPedido,
  disponibles: LineaHistorial[],
  usadas: Set<LineaHistorial>,
): LineaHistorial | null {
  const libres = disponibles.filter((anterior) => !usadas.has(anterior))

  if (linea.product_id != null) {
    const porId = libres.find((anterior) => anterior.product_id === linea.product_id)
    if (porId) return porId
  }

  const buscada = clave(linea.nombre)
  return libres.find((anterior) => clave(anterior.nombre) === buscada) ?? null
}

function lineasComparables(historial: Historial | null | undefined): LineaHistorial[] {
  if (!historial || !historial.encontrado) return []
  return historial.lineas ?? []
}

export function compararPrecios(
  lineas: LineaPedido[],
  historial: Historial | null | undefined,
): ComparacionLinea[] {
  const anteriores = lineasComparables(historial)
  const usadas = new Set<LineaHistorial>()

  return lineas.map((linea) => {
    const anterior = emparejar(linea, anteriores, usadas)
    if (anterior) usadas.add(anterior)
    const precioAnterior = anterior ? anterior.precio : null

    return {
      nombre: linea.nombre,
      cantidad: linea.cantidad,
      precio: linea.precio,
      precioAnterior,
      difiere: precioAnterior !== null && Math.abs(precioAnterior - linea.precio) >= TOLERANCIA,
      subtotal: redondear(linea.cantidad * linea.precio),
    }
  })
}

export function hayDiferencia(comparaciones: ComparacionLinea[]): boolean {
  return comparaciones.some((linea) => linea.difiere)
}

function redondear(valor: number): number {
  return Math.round(valor * 100) / 100
}

/** La base imponible del pedido que se va a publicar. La devolucion resta. */
export function totalBase(lineas: LineaPedido[]): number {
  return redondear(lineas.reduce((suma, linea) => suma + linea.cantidad * linea.precio, 0))
}

/**
 * La base imponible del pedido anterior, sumando sus lineas.
 *
 * No se usa `Historial.total`, que es el `amount_total` de Odoo **con IVA**:
 * enfrentarlo a la base imponible del pedido nuevo marcaria una diferencia del
 * 21% que no existe.
 */
export function totalAnterior(historial: Historial | null | undefined): number | null {
  const lineas = lineasComparables(historial)
  if (!historial || !historial.encontrado) return null
  return redondear(lineas.reduce((suma, linea) => suma + linea.subtotal, 0))
}
